import { useState, useEffect, useCallback, useRef } from 'react';
import { Auth } from 'aws-amplify';
import type { WebSocketMessage } from '../../../services/websocket.service';

type MessageHandler = (data: WebSocketMessage) => void;

export interface UseWebSocketConnectionReturn {
  isConnected: boolean;
  connectionError: string | null;
  sendRaw: (payload: Record<string, unknown>) => void;
  addMessageHandler: (id: string, handler: MessageHandler) => void;
  removeMessageHandler: (id: string) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1500;
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Reconnect 5 min before token expiry

async function getFreshTokens(): Promise<{ accessToken: string; idToken: string; expiresAtMs: number }> {
  const session = await Auth.currentSession();
  const idTokenObj = session.getIdToken();
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: idTokenObj.getJwtToken(),
    expiresAtMs: idTokenObj.getExpiration() * 1000,
  };
}

export const useWebSocketConnection = (): UseWebSocketConnectionReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Map<string, MessageHandler>>(new Map());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenExpiresAtRef = useRef<number>(0);
  const isReconnectingRef = useRef(false);
  const cancelledRef = useRef(false);

  const addMessageHandler = useCallback((id: string, handler: MessageHandler) => {
    handlersRef.current.set(id, handler);
  }, []);

  const removeMessageHandler = useCallback((id: string) => {
    handlersRef.current.delete(id);
  }, []);

  const clearTokenRefreshTimer = useCallback(() => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (cancelledRef.current) return;

    // Guard: prevent concurrent connect() calls (racing reconnects)
    if (isReconnectingRef.current) {
      console.log('[WS] connect() already in progress — skipping');
      return;
    }
    isReconnectingRef.current = true;

    const settings = (window as any).__ADDAPT_SETTINGS__;
    const wsUrl = settings?.webSocketConfig?.ai || settings?.webSocketConfig?.default;

    if (!wsUrl) {
      console.error('[WS] No WebSocket URL configured in settings');
      setConnectionError('WebSocket not configured. Check settings.');
      isReconnectingRef.current = false;
      return;
    }

    try {
      const { accessToken, idToken, expiresAtMs } = await getFreshTokens();
      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(accessToken)}&idToken=${encodeURIComponent(idToken)}`;

      if (cancelledRef.current) { isReconnectingRef.current = false; return; }

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, 'Replacing connection');
        wsRef.current = null;
      }

      tokenExpiresAtRef.current = expiresAtMs;

      // Reset reconnect counter for proactive token-refresh reconnects
      // so prior transient failures don't block the refresh
      reconnectAttemptsRef.current = 0;

      // Schedule proactive reconnect before token expires
      clearTokenRefreshTimer();
      const timeUntilExpiry = expiresAtMs - Date.now();
      const reconnectIn = Math.max(timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS, 30_000);
      console.log(`[WS] Token expires in ${Math.round(timeUntilExpiry / 60000)}min — scheduling reconnect in ${Math.round(reconnectIn / 60000)}min`);
      tokenRefreshTimerRef.current = setTimeout(() => {
        if (cancelledRef.current) return;
        console.log('[WS] Proactive token-refresh reconnect');
        connect();
      }, reconnectIn);

      console.log('[WS] Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrlWithToken);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelledRef.current) { ws.close(); return; }
        console.log('[WS] WebSocket connected');
        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        setIsConnected(true);
        setConnectionError(null);

        // Start keepalive ping every 30s to prevent idle disconnect
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, 30_000);

        // Expose sendRaw globally for metrics.ts (avoids circular import)
        (window as any).__ADDAPT_WS_SEND_RAW__ = (data: Record<string, unknown>) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(data));
          }
        };
      };

      ws.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          if (data.type === 'pong') return; // Keepalive response, ignore
          Array.from(handlersRef.current.values()).forEach(handler => {
            handler(data);
          });
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('[WS] WebSocket closed:', event.code, event.reason);
        if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        (window as any).__ADDAPT_WS_SEND_RAW__ = undefined;
        setIsConnected(false);
        wsRef.current = null;
        isReconnectingRef.current = false;

        if (cancelledRef.current) return;

        if (event.code === 1008) {
          console.error('[WS] Auth failure (1008) — will refresh token and retry');
        }

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
          setConnectionError(`Reconnecting... (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimerRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          console.error('[WS] Max reconnect attempts reached');
          setConnectionError('Connection lost. Please refresh the page.');
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] WebSocket error:', err);
      };
    } catch (err: any) {
      console.error('[WS] Auth/connect failed:', err);
      isReconnectingRef.current = false;

      if (cancelledRef.current) return;

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
        reconnectAttemptsRef.current += 1;
        console.log(`[WS] Auth failed, retrying in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
        setConnectionError(`Authentication error — retrying...`);

        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        setConnectionError(err?.message ?? 'Failed to connect');
      }
    }
  }, [clearTokenRefreshTimer]);

  useEffect(() => {
    cancelledRef.current = false;
    connect();

    return () => {
      cancelledRef.current = true;
      clearTokenRefreshTimer();
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [connect, clearTokenRefreshTimer]);

  const ensureFreshConnection = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    const bufferMs = 60_000; // 1 min safety buffer
    if (tokenExpiresAtRef.current > 0 && tokenExpiresAtRef.current - now < bufferMs) {
      console.log('[WS] Token expired or expiring soon — reconnecting before send');
      if (!isReconnectingRef.current) {
        isReconnectingRef.current = true;
        await connect();
      }
      return wsRef.current?.readyState === WebSocket.OPEN;
    }
    return true;
  }, [connect]);

  const sendRaw = useCallback((payload: Record<string, unknown>) => {
    const doSend = () => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(payload));
      } else {
        console.error('[WS] Cannot send: WebSocket not open. readyState:', ws?.readyState);
        setConnectionError('Not connected. Attempting to reconnect...');
        if (reconnectAttemptsRef.current === 0 && !reconnectTimerRef.current) {
          connect();
        }
      }
    };

    const now = Date.now();
    const bufferMs = 60_000;
    if (tokenExpiresAtRef.current > 0 && tokenExpiresAtRef.current - now < bufferMs) {
      ensureFreshConnection().then(() => doSend());
    } else {
      doSend();
    }
  }, [connect, ensureFreshConnection]);

  return {
    isConnected,
    connectionError,
    sendRaw,
    addMessageHandler,
    removeMessageHandler,
  };
};
